import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { DateRangePicker } from '@/components/DateRangePicker'
import type { StoreBrand, StoreCategory } from '@/api/products'
import { CategoryTreeSelect } from './CategoryTreeSelect'

interface ProductFiltersProps {
	fieldKey: string
	keyword: string
	cateId: number
	brandId: number
	priceMin: string
	priceMax: string
	stockMin: string
	stockMax: string
	salesMin: string
	salesMax: string
	createTimeMin: string
	createTimeMax: string
	salesOrder: string
	specType: number
	brands: StoreBrand[]
	categories: StoreCategory[]
	onFieldKeyChange: (v: string) => void
	onKeywordChange: (v: string) => void
	onCateIdChange: (v: number) => void
	onBrandIdChange: (v: number) => void
	onPriceMinChange: (v: string) => void
	onPriceMaxChange: (v: string) => void
	onStockMinChange: (v: string) => void
	onStockMaxChange: (v: string) => void
	onSalesMinChange: (v: string) => void
	onSalesMaxChange: (v: string) => void
	onCreateTimeMinChange: (v: string) => void
	onCreateTimeMaxChange: (v: string) => void
	onSalesOrderChange: (v: string) => void
	onSpecTypeChange: (v: number) => void
	onSearch: () => void
	onReset: () => void
}

export function ProductFilters({
	fieldKey, keyword, cateId, brandId, priceMin, priceMax,
	stockMin, stockMax, salesMin, salesMax, createTimeMin, createTimeMax, salesOrder, specType,
	brands, categories,
	onFieldKeyChange, onKeywordChange, onCateIdChange, onBrandIdChange,
	onPriceMinChange, onPriceMaxChange,
	onStockMinChange, onStockMaxChange,
	onSalesMinChange, onSalesMaxChange,
	onCreateTimeMinChange, onCreateTimeMaxChange,
	onSalesOrderChange,
	onSpecTypeChange,
	onSearch, onReset,
}: ProductFiltersProps) {
	return (
		<div className="flex flex-wrap items-end gap-3 p-4 bg-card rounded-lg border">
			{/* 搜索类型 + 关键词 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">搜索类型</span>
				<Select value={fieldKey || 'store_name'} onValueChange={onFieldKeyChange}>
					<SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem value="store_name">商品名称</SelectItem>
						<SelectItem value="product_id">商品ID</SelectItem>
						<SelectItem value="keyword">关键字</SelectItem>
						<SelectItem value="bar_code">条码</SelectItem>
						<SelectItem value="code">编码</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">关键词</span>
				<Input
					className="w-[200px]"
					placeholder="输入关键词"
					value={keyword}
					onChange={(e) => onKeywordChange(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && onSearch()}
				/>
			</div>

			{/* 分类（树形） */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">分类</span>
				<CategoryTreeSelect
					categories={categories}
					selectedId={cateId}
					onSelect={onCateIdChange}
				/>
			</div>

			{/* 品牌 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">品牌</span>
				<Select value={brandId ? String(brandId) : '0'} onValueChange={(v) => onBrandIdChange(Number(v))}>
					<SelectTrigger className="w-[140px]"><SelectValue placeholder="全部品牌" /></SelectTrigger>
					<SelectContent>
						<SelectItem value="0">全部品牌</SelectItem>
						{brands.map((b) => (
							<SelectItem key={b.id} value={String(b.id)}>{b.brand_name}</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* 价格区间 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">价格区间</span>
				<div className="flex items-center gap-1">
					<Input className="w-[72px]" type="number" placeholder="最低" value={priceMin} onChange={(e) => onPriceMinChange(e.target.value)} />
					<span className="text-muted-foreground">-</span>
					<Input className="w-[72px]" type="number" placeholder="最高" value={priceMax} onChange={(e) => onPriceMaxChange(e.target.value)} />
				</div>
			</div>

			{/* 库存区间 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">库存区间</span>
				<div className="flex items-center gap-1">
					<Input className="w-[72px]" type="number" placeholder="最低" value={stockMin} onChange={(e) => onStockMinChange(e.target.value)} />
					<span className="text-muted-foreground">-</span>
					<Input className="w-[72px]" type="number" placeholder="最高" value={stockMax} onChange={(e) => onStockMaxChange(e.target.value)} />
				</div>
			</div>

			{/* 销量区间 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">销量区间</span>
				<div className="flex items-center gap-1">
					<Input className="w-[72px]" type="number" placeholder="最低" value={salesMin} onChange={(e) => onSalesMinChange(e.target.value)} />
					<span className="text-muted-foreground">-</span>
					<Input className="w-[72px]" type="number" placeholder="最高" value={salesMax} onChange={(e) => onSalesMaxChange(e.target.value)} />
				</div>
			</div>

			{/* 创建时间 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">创建时间</span>
				<DateRangePicker
					value={{ start_date: createTimeMin || undefined, end_date: createTimeMax || undefined }}
					onChange={(v) => { onCreateTimeMinChange(v.start_date || ''); onCreateTimeMaxChange(v.end_date || '') }}
				/>
			</div>

			{/* 规格类型 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">商品规格</span>
				<Select value={specType === -1 ? 'all' : String(specType)} onValueChange={(v) => onSpecTypeChange(v === 'all' ? -1 : Number(v))}>
					<SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem value="all">全部</SelectItem>
						<SelectItem value="0">单规格</SelectItem>
						<SelectItem value="1">多规格</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* 销量排序 */}
			<div className="flex flex-col gap-1">
				<span className="text-xs text-muted-foreground">销量排序</span>
				<Select value={salesOrder} onValueChange={onSalesOrderChange}>
					<SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem value="normal">默认</SelectItem>
						<SelectItem value="desc">高到低</SelectItem>
						<SelectItem value="asc">低到高</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{/* 操作 */}
			<div className="flex gap-2">
				<Button onClick={onSearch}>搜索</Button>
				<Button variant="outline" onClick={onReset}>重置</Button>
			</div>
		</div>
	)
}
